import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

/**
 * Module-level access token managed by <ApiTokenSync />.
 * The request interceptor attaches it as a Bearer header on every outbound call.
 */
let _accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  _accessToken = token;
}

/**
 * Callback set by <ApiTokenSync /> so the interceptor can request a fresh token
 * from oidc-client-ts when the API returns 401.
 */
let _refreshToken: (() => Promise<string | null>) | null = null;

export function setTokenRefresher(fn: (() => Promise<string | null>) | null) {
  _refreshToken = fn;
}

/**
 * Shared 401 response handler: attempts one silent token refresh then retries
 * the original request.  If the refresh itself fails the error propagates.
 *
 * Anonymous callers (no access token) have no session to refresh, so their
 * 401s fail fast: a silent sign-in would otherwise wait out oidc-client's
 * iframe timeout (10 s) and stall public pages such as the calculator.
 */
async function handle401Retry(error: AxiosError) {
  if (error.response?.status !== 401) return Promise.reject(error);

  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
  if (originalRequest._retry || !_refreshToken || !_accessToken) return Promise.reject(error);

  originalRequest._retry = true;
  try {
    const newToken = await _refreshToken();
    if (newToken) {
      _accessToken = newToken;
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axios(originalRequest);
    }
  } catch (refreshError) {
    console.error("[apiClient] token refresh failed on 401 retry:", refreshError);
  }
  return Promise.reject(error);
}

/** Gateway statuses worth retrying: the request never reached a healthy API. */
const TRANSIENT_STATUSES = new Set([502, 503, 504]);

/**
 * Waits before each retry of a GET that hit a gateway error or a dropped
 * connection. About 4.6 s in total: enough to ride out a web-client container
 * that has just started and not yet resolved the API host (nginx upstream).
 */
export const TRANSIENT_RETRY_DELAYS_MS = [400, 1200, 3000];

/**
 * Retries idempotent GETs on 502/503/504 or a connection that never got a
 * response. POSTs (forms) are never retried, and neither are 4xx (incl. 429),
 * timeouts or cancellations.
 */
function transientRetry(instance: AxiosInstance) {
  return async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & { _transientRetries?: number }) | undefined;
    if (!config || (config.method ?? "get").toLowerCase() !== "get") return Promise.reject(error);

    const status = error.response?.status;
    const dropped = status === undefined && error.code === "ERR_NETWORK";
    if (!dropped && !(status !== undefined && TRANSIENT_STATUSES.has(status))) return Promise.reject(error);

    const attempt = config._transientRetries ?? 0;
    if (attempt >= TRANSIENT_RETRY_DELAYS_MS.length) return Promise.reject(error);
    config._transientRetries = attempt + 1;
    await new Promise((resolve) => setTimeout(resolve, TRANSIENT_RETRY_DELAYS_MS[attempt]));
    return instance.request(config);
  };
}

/**
 * Pre-configured Axios instance for NellsPay API v1.
 *
 * In development the Vite dev-server proxies `/api` to the backend
 * (see vite.config.ts), so a relative baseURL is sufficient.
 */
const apiClient = axios.create({
  baseURL: "/api/v1",
  headers: { "Content-Type": "application/json" },
  // A hung upstream must not block the caller forever (§7.5 resilience).
  timeout: 30000,
});

// ── Request: attach bearer token ────────────────────────────────────────────
apiClient.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

// ── Response: retry once on 401 with a refreshed token ─────────────────────
apiClient.interceptors.response.use((r) => r, handle401Retry);
// ── Response: ride out gateway errors on GETs (see transientRetry) ──────────
apiClient.interceptors.response.use((r) => r, transientRetry(apiClient));

/**
 * Pre-configured Axios instance for NellsPay API v2 (Reporting).
 * Shares the same auth token and error handling as apiClient.
 */
export const apiClientV2 = axios.create({
  baseURL: "/api/v2",
  headers: { "Content-Type": "application/json" },
  // A hung upstream must not block the caller forever (§7.5 resilience).
  timeout: 30000,
});

apiClientV2.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

apiClientV2.interceptors.response.use((r) => r, handle401Retry);
apiClientV2.interceptors.response.use((r) => r, transientRetry(apiClientV2));

export default apiClient;
